import { Download, FileUp, Upload } from "lucide-react";
import Papa from "papaparse";
import { useState } from "react";
import { toast } from "sonner";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ApiError, apiFetch } from "@/lib/api";

const HEADERS = [
  "assetCode",
  "materialCode",
  "serialNumber",
  "financialCardNo",
  "hostname",
  "inboundDate",
  "purchaseUnitPriceYuan",
  "locationText",
  "warrantyExpiry",
  "purchaseOrderNo",
  "contractNo",
  "modelSpec",
  "condition",
  "acceptanceRequired",
];

interface PreviewRow {
  assetCode: string;
  materialCode: string;
  serialNumber: string | null;
  inboundDate: string;
}

interface Result {
  valid: boolean;
  errors: { row: number; message: string }[];
  preview: PreviewRow[];
  total: number;
  created: number;
}

export function AssetImportDialog({
  open,
  onOpenChange,
  onImported,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onImported?: () => void;
}) {
  const [fileName, setFileName] = useState("");
  const [rows, setRows] = useState<Record<string, unknown>[]>([]);
  const [result, setResult] = useState<Result | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<"create_only" | "upsert">("create_only");
  const [busy, setBusy] = useState(false);

  function reset() {
    setFileName("");
    setRows([]);
    setResult(null);
    setError(null);
  }

  function downloadTemplate() {
    const csv = Papa.unparse([Object.fromEntries(HEADERS.map((header) => [header, ""]))]);
    const blob = new Blob([`\uFEFF${csv}`], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "asset-import-template.csv";
    link.click();
    URL.revokeObjectURL(url);
  }

  function parseFile(file: File) {
    setError(null);
    setResult(null);
    setFileName(file.name);
    Papa.parse<Record<string, unknown>>(file, {
      header: true,
      skipEmptyLines: true,
      complete: (parsed) => {
        if (parsed.errors.length > 0) {
          setError(`CSV 解析失败：${parsed.errors[0]?.message ?? "未知错误"}`);
          return;
        }
        const data = parsed.data.filter((row) =>
          Object.values(row).some((value) => String(value ?? "").trim() !== ""),
        );
        if (data.length > 200) {
          setError("单次最多 200 行，请拆分文件");
          return;
        }
        setRows(data);
      },
      error: (parseError) => setError(`CSV 读取失败：${parseError.message}`),
    });
  }

  function downloadFailedRows() {
    if (!result?.errors.length) return;
    const errorByRow = new Map(result.errors.map((item) => [item.row, item.message]));
    const rowsWithErrors = rows
      .map((row, index) => ({
        ...row,
        _error: errorByRow.get(index + 2) ?? "",
      }))
      .filter((row) => row._error);
    const csv = Papa.unparse(rowsWithErrors);
    const blob = new Blob([`\uFEFF${csv}`], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "asset-import-failed.csv";
    link.click();
    URL.revokeObjectURL(url);
  }

  async function validate() {
    setBusy(true);
    setError(null);
    try {
      const response = await apiFetch<Result>("/api/v1/assets/import", {
        method: "POST",
        body: JSON.stringify({ dryRun: true, mode, rows }),
      });
      setResult(response);
      if (response.valid) toast.success(`校验通过，共 ${response.total} 行`);
      else toast.error(`有 ${response.errors.length} 行需要修正`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "校验失败");
    } finally {
      setBusy(false);
    }
  }

  async function commit() {
    if (!result?.valid) return;
    setBusy(true);
    try {
      const response = await apiFetch<Result>("/api/v1/assets/import", {
        method: "POST",
        body: JSON.stringify({ dryRun: false, mode, rows }),
      });
      toast.success(`已导入 ${response.created} 台资产`);
      onImported?.();
      onOpenChange(false);
      reset();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "导入失败");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>批量导入资产</DialogTitle>
          <DialogDescription>
            按物料编码逐台导入；资产编号由系统生成，序列号/财务卡片号重复会拦截。
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap gap-2">
          <select
            className="h-9 rounded-md border border-input bg-background px-3 text-sm"
            value={mode}
            onChange={(event) => {
              setMode(event.target.value as "create_only" | "upsert");
              setResult(null);
            }}
          >
            <option value="create_only">仅新增</option>
            <option value="upsert">按资产编号更新/新增</option>
          </select>
          <Button variant="outline" onClick={downloadTemplate}>
            <Download className="h-4 w-4" />
            下载 CSV 模板
          </Button>
          <label className="inline-flex h-9 cursor-pointer items-center gap-2 rounded-md border border-input bg-background px-4 text-sm font-medium hover:bg-muted">
            <FileUp className="h-4 w-4" />
            选择 CSV
            <input
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) parseFile(file);
              }}
            />
          </label>
          <Button onClick={validate} disabled={rows.length === 0 || busy}>
            <Upload className="h-4 w-4" />
            先校验
          </Button>
          {result && result.errors.length > 0 ? (
            <Button variant="outline" onClick={downloadFailedRows}>
              <Download className="h-4 w-4" />
              下载失败行
            </Button>
          ) : null}
        </div>
        {fileName ? (
          <p className="text-xs text-muted-foreground">
            已选择：{fileName}（{rows.length} 行）
          </p>
        ) : null}
        {error ? (
          <Alert variant="danger" title="导入失败">
            {error}
          </Alert>
        ) : null}
        {result && !result.valid ? (
          <div className="max-h-40 overflow-auto rounded-md border border-red-200 bg-red-50 p-3 text-xs text-red-700">
            {result.errors.slice(0, 30).map((item) => (
              <p key={`${item.row}-${item.message}`}>
                第 {item.row} 行：{item.message}
              </p>
            ))}
          </div>
        ) : null}
        {result?.valid && result.preview.length > 0 ? (
          <div className="max-h-64 overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>资产编号</TableHead>
                  <TableHead>物料</TableHead>
                  <TableHead>序列号</TableHead>
                  <TableHead>入库日期</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {result.preview.map((row) => (
                  <TableRow key={row.assetCode}>
                    <TableCell className="font-mono text-xs">{row.assetCode}</TableCell>
                    <TableCell>{row.materialCode}</TableCell>
                    <TableCell>{row.serialNumber ?? "—"}</TableCell>
                    <TableCell>{row.inboundDate}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : null}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            取消
          </Button>
          <Button onClick={commit} disabled={!result?.valid || busy}>
            确认导入
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
